import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DayLoader } from "@/components/DayLoader";
import { IssueGrid } from "@/components/IssueGrid";
import { POS } from "@/components/POS";
import { SectionHead } from "@/components/SectionHead";
import { Seg } from "@/components/Seg";
import { Toggle } from "@/components/Toggle";
import { WordRow } from "@/components/WordRow";
import { useIssueViews } from "@/hooks/useIssueViews";

export function DesignSystem() {
  const navigate = useNavigate();
  const issues = useIssueViews();

  return (
    <div className="page">
      <SectionHead
        kicker="Design system · v0.1"
        title={
          <>
            The <span className="italic">system</span>.
          </>
        }
        right={
          <div className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
            Newsreader · Instrument Sans · JetBrains Mono
          </div>
        }
      />

      <section className="container" style={{ maxWidth: 720, paddingBottom: "var(--s-7)" }}>
        <p className="t-display" style={{ fontSize: "var(--t-lg)", color: "var(--text-dim)", lineHeight: 1.6 }}>
          LEX · 30 is built like a <em>printed almanac</em> rather than a learning app. Numbered issues,
          indexed entries, a serif voice for the words, a monospace voice for the metadata, and a
          sans-serif voice for the controls. Color is used <em>sparingly</em> — three accent dots signal
          the mastery state of every word in the corpus.
        </p>
      </section>

      <section className="container ds-section">
        <DSHead num="01" title="Typography" subtitle="Three voices · serif · sans · mono" />
        <div className="ds-type">
          {TYPE_ROWS.map(([kicker, body, opts]) => (
            <div key={kicker} className="ds-type-row">
              <div className="t-eyebrow">{kicker}</div>
              <div className={opts.cls} style={opts.style}>{body}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="container ds-section">
        <DSHead num="02" title="Color" subtitle="Warm bone paper · warm ink · 4 accents" />
        <div className="t-eyebrow" style={{ marginBottom: 8 }}>Paper · neutrals</div>
        <div className="swatch-row">
          {["--paper-0", "--paper-1", "--paper-2", "--paper-3", "--paper-4"].map((v) => (
            <Swatch key={v} cssVar={v} />
          ))}
        </div>
        <div className="t-eyebrow" style={{ marginTop: 24, marginBottom: 8 }}>Ink · text</div>
        <div className="swatch-row">
          {["--ink-0", "--ink-1", "--ink-2", "--ink-3"].map((v) => (
            <Swatch key={v} cssVar={v} />
          ))}
        </div>
        <div className="t-eyebrow" style={{ marginTop: 24, marginBottom: 8 }}>Accents · oklch · same chroma</div>
        <div className="swatch-row">
          <Swatch cssVar="--accent-red" label="Unknown / new" />
          <Swatch cssVar="--accent-amber" label="Learning / streak" />
          <Swatch cssVar="--accent-moss" label="Mastered / done" />
          <Swatch cssVar="--accent-indigo" label="Due review" />
        </div>
      </section>

      <section className="container ds-section">
        <DSHead num="03" title="Spacing & Rhythm" subtitle="8px base · 10 steps" />
        <div className="stack" style={{ gap: 10 }}>
          {SPACING.map(([v, n]) => (
            <div key={v} className="row-c" style={{ gap: 16 }}>
              <span className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)", width: 60 }}>{v}</span>
              <span className="t-mono" style={{ fontSize: 11, color: "var(--text-faint)", width: 40 }}>{n}px</span>
              <span style={{ height: 10, width: n, background: "var(--ink-0)" }} />
            </div>
          ))}
        </div>
      </section>

      <section className="container ds-section">
        <DSHead num="04" title="Components" subtitle="The kit, in situ" />

        <div className="ds-comp">
          <div className="t-eyebrow">Buttons</div>
          <div className="row-c" style={{ gap: 8, flexWrap: "wrap" }}>
            <button className="btn">Default</button>
            <button className="btn btn-solid">Primary</button>
            <button className="btn btn-ghost">Ghost</button>
            <button className="btn btn-sm">Small</button>
            <button className="btn btn-lg">Large</button>
            <button className="btn-icon">↻</button>
          </div>
        </div>

        <div className="ds-comp">
          <div className="t-eyebrow">Pills & Tags</div>
          <div className="row-c" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="pill">META</span>
            <span className="pill pill-solid">PRIMARY</span>
            <POS value="n" />
            <POS value="v" />
            <POS value="adj" />
            <POS value="n / v" />
          </div>
        </div>

        <div className="ds-comp">
          <div className="t-eyebrow">Status dots</div>
          <div className="row-c" style={{ gap: 16, flexWrap: "wrap" }}>
            {DOTS.map(([d, l]) => (
              <span key={d} className="row-c" style={{ gap: 6 }}>
                <span className={`dot dot--${d}`} />
                <span className="t-meta" style={{ fontSize: 11 }}>{l}</span>
              </span>
            ))}
          </div>
        </div>

        <div className="ds-comp">
          <div className="t-eyebrow">Chips</div>
          <div className="chip-row">
            <button className="chip is-on">All</button>
            <button className="chip">Active</button>
            <button className="chip">Completed</button>
            <button className="chip">Upcoming</button>
          </div>
        </div>

        <div className="ds-comp">
          <div className="t-eyebrow">Segmented control · toggle</div>
          <div className="row-c" style={{ gap: 12, flexWrap: "wrap" }}>
            <Seg
              value="M"
              onChange={() => {}}
              options={[
                ["S", "S"],
                ["M", "M"],
                ["L", "L"],
                ["XL", "XL"],
              ]}
            />
            <Toggle value={true} onChange={() => {}} />
          </div>
        </div>

        <div className="ds-comp">
          <div className="t-eyebrow">Issue card</div>
          <IssueGrid
            issues={issues.slice(0, 4)}
            onOpen={(d) => navigate(`/day/${d}`)}
            minCol={200}
          />
        </div>

        <div className="ds-comp">
          <div className="t-eyebrow">Word row · sampled from Day 01</div>
          <DayLoader day={1}>
            {(data) => (
              <div style={{ borderTop: "var(--border-thin)" }}>
                {data.words.slice(0, 3).map((w) => (
                  <WordRow
                    key={w.index}
                    word={w}
                    mastery="untouched"
                    onOpen={(idx) => navigate(`/word/1/${idx}`)}
                  />
                ))}
              </div>
            )}
          </DayLoader>
        </div>
      </section>
    </div>
  );
}

const TYPE_ROWS: ReadonlyArray<readonly [string, React.ReactNode, { cls: string; style?: React.CSSProperties }]> = [
  ["Display · Newsreader", <>Display 68px · <span className="italic">italic</span></>, {
    cls: "t-display",
    style: { fontSize: "var(--t-4xl)", letterSpacing: "-0.03em", lineHeight: 1 },
  }],
  ["Heading · Newsreader", "Heading 48px", { cls: "t-display", style: { fontSize: "var(--t-3xl)" } }],
  ["Subheading · Newsreader", <>Subheading 26px · <span className="italic">vocabulary</span></>, {
    cls: "t-display",
    style: { fontSize: "var(--t-xl)" },
  }],
  [
    "Body · Newsreader",
    "Body 17px · A thirty-day vocabulary almanac, organized into 30 thematic issues of 40 entries each.",
    { cls: "t-display", style: { fontSize: "var(--t-md)", lineHeight: 1.6, color: "var(--text-dim)" } },
  ],
  ["UI · Instrument Sans", "UI · 17px · Resume reading · Open issue · Begin quiz", { cls: "t-sans", style: { fontSize: "var(--t-md)" } }],
  ["Mono · JetBrains Mono", "0123 · Nº 01 · ENTRY 003 / 040 · /əˈraɪvl/", { cls: "t-mono", style: { fontSize: "var(--t-md)" } }],
  ["Meta · uppercase mono", "ISSUE Nº 01 · TOURISM · 40 ENTRIES", { cls: "t-meta" }],
];

const SPACING: ReadonlyArray<readonly [string, number]> = [
  ["--s-1", 4],
  ["--s-2", 8],
  ["--s-3", 12],
  ["--s-4", 16],
  ["--s-5", 24],
  ["--s-6", 32],
  ["--s-7", 48],
  ["--s-8", 64],
  ["--s-9", 96],
  ["--s-10", 128],
];

const DOTS: ReadonlyArray<readonly [string, string]> = [
  ["red", "Unknown"],
  ["amber", "Learning"],
  ["moss", "Known"],
  ["indigo", "Due review"],
  ["empty", "Locked"],
];

function DSHead({ num, title, subtitle }: { num: string; title: string; subtitle?: string }) {
  return (
    <div style={{ marginBottom: "var(--s-5)", borderBottom: "var(--border-thin)", paddingBottom: "var(--s-3)" }}>
      <div className="row-b" style={{ gap: 12 }}>
        <span className="t-mono" style={{ fontSize: 12, color: "var(--text-faint)" }}>§ {num}</span>
        <h2 className="t-display" style={{ fontSize: "var(--t-2xl)", margin: 0, letterSpacing: "-0.02em" }}>
          {title}
        </h2>
      </div>
      {subtitle && (
        <p
          className="t-mono"
          style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 0 0", letterSpacing: "0.04em" }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

function Swatch({ cssVar, label }: { cssVar: string; label?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [, setHex] = useState("");
  useEffect(() => {
    if (ref.current) {
      setHex(getComputedStyle(ref.current).backgroundColor);
    }
  }, []);
  return (
    <div className="swatch">
      <div className="swatch__color" ref={ref} style={{ background: `var(${cssVar})` }} />
      <div className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>{cssVar}</div>
      {label && <div className="t-mono" style={{ fontSize: 10, color: "var(--text-faint)" }}>{label}</div>}
    </div>
  );
}
